import React, { useState, useEffect, useCallback } from 'react';
import { AdminLayout } from '../components/AdminLayout';
import { getAllVideosAdmin, adminDeleteVideo, approveVideo, updateVideoStatus } from '../api';
import { toast } from 'sonner';
import { Search, Trash2, CheckCircle, Eye, Clock } from 'lucide-react';

const STATUS_OPTIONS = ['new', 'trending', 'featured', 'soon'];
const CATEGORIES = [
  '', 'music', 'sports', 'education', 'entertainment', 'news',
  'documentary', 'short_film', 'feature_film', 'animation',
  'reality_tv', 'show', 'comedy', 'religion', 'cooking', 'fashion', 'tech', 'general'
];

const CATEGORY_LABELS = {
  '': 'Toutes catégories',
  music: '🎵 Musique', sports: '⚽ Sports', education: '📚 Éducation',
  entertainment: '🎭 Divertissement', news: '📰 Actualités', documentary: '🎞️ Documentaire',
  short_film: '🎥 Court métrage', feature_film: '🎦 Long métrage', animation: '🎨 Animation',
  reality_tv: '📺 Téléréalité', show: '🎤 Émission', comedy: '😂 Comédie',
  religion: '✝️ Religion', cooking: '🍳 Cuisine', fashion: '👗 Mode', tech: '💻 Technologie',
  general: '🎬 Général',
};

export const AdminVideosPage = () => {
  const [videos, setVideos] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [approvedFilter, setApprovedFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [page, setPage] = useState(1);
  const limit = 20;

  const fetchVideos = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit };
      if (search) params.search = search;
      if (approvedFilter !== '') params.approved = approvedFilter;
      if (categoryFilter) params.category = categoryFilter;
      const res = await getAllVideosAdmin(params);
      setVideos(res.data.videos);
      setTotal(res.data.total);
    } catch {
      toast.error('Erreur chargement vidéos');
    } finally {
      setLoading(false);
    }
  }, [search, approvedFilter, categoryFilter, page]);

  useEffect(() => { fetchVideos(); }, [fetchVideos]);

  const handleApprove = async (videoId) => {
    try {
      await approveVideo(videoId);
      toast.success('Vidéo approuvée');
      fetchVideos();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const handleStatusChange = async (videoId, status) => {
    try {
      await updateVideoStatus(videoId, status);
      toast.success('Statut mis à jour');
      fetchVideos();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const handleDelete = async (videoId, title) => {
    if (!window.confirm(`Supprimer "${title}" ? Irréversible.`)) return;
    try {
      await adminDeleteVideo(videoId);
      toast.success('Vidéo supprimée');
      fetchVideos();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Vidéos</h1>
          <p className="text-muted-foreground text-sm">{total} vidéos au total</p>
        </div>

        {/* Filtres */}
        <div className="flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-52">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              className="w-full pl-9 pr-4 py-2 bg-card border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="Rechercher titre ou créateur..."
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
            />
          </div>
          <select
            className="px-3 py-2 bg-card border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            value={approvedFilter}
            onChange={e => { setApprovedFilter(e.target.value); setPage(1); }}
          >
            <option value="">Tout état</option>
            <option value="true">Approuvées</option>
            <option value="false">En attente</option>
          </select>
          <select
            className="px-3 py-2 bg-card border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            value={categoryFilter}
            onChange={e => { setCategoryFilter(e.target.value); setPage(1); }}
          >
            {CATEGORIES.map(c => <option key={c} value={c}>{CATEGORY_LABELS[c] || c}</option>)}
          </select>
          <button onClick={fetchVideos} className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm hover:bg-primary/90 transition-colors">
            Filtrer
          </button>
        </div>

        {/* Table */}
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          {loading ? (
            <div className="py-16 text-center text-muted-foreground">Chargement...</div>
          ) : videos.length === 0 ? (
            <div className="py-16 text-center text-muted-foreground">Aucune vidéo trouvée</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/30">
                    <th className="px-4 py-3 text-left text-muted-foreground font-medium">Vidéo</th>
                    <th className="px-4 py-3 text-left text-muted-foreground font-medium">Créateur</th>
                    <th className="px-4 py-3 text-left text-muted-foreground font-medium">État</th>
                    <th className="px-4 py-3 text-left text-muted-foreground font-medium">Statut</th>
                    <th className="px-4 py-3 text-left text-muted-foreground font-medium">Vues</th>
                    <th className="px-4 py-3 text-right text-muted-foreground font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {videos.map(v => (
                    <tr key={v.id} className="hover:bg-muted/20 transition-colors">
                      <td className="px-4 py-3">
                        <div className="font-medium text-foreground max-w-xs truncate">{v.title}</div>
                        <div className="text-xs text-muted-foreground">{v.category}</div>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">{v.creator_name || v.creator_id}</td>
                      <td className="px-4 py-3">
                        {v.is_approved ? (
                          <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full flex items-center gap-1 w-fit">
                            <CheckCircle className="w-3 h-3" /> Approuvée
                          </span>
                        ) : (
                          <div className="flex items-center gap-2">
                            <span className="px-2 py-0.5 bg-yellow-500/20 text-yellow-400 text-xs rounded-full flex items-center gap-1 w-fit">
                              <Clock className="w-3 h-3" /> En attente
                            </span>
                            <button
                              onClick={() => handleApprove(v.id)}
                              className="text-xs px-2 py-0.5 bg-green-500/20 text-green-400 rounded hover:bg-green-500/40 transition-colors"
                            >
                              Approuver
                            </button>
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <select
                          className="text-xs bg-transparent border border-border rounded px-2 py-1 text-foreground focus:outline-none"
                          value={v.status || 'new'}
                          onChange={e => handleStatusChange(v.id, e.target.value)}
                        >
                          {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">
                        <div className="flex items-center gap-1"><Eye className="w-3 h-3" />{v.views || 0}</div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => handleDelete(v.id, v.title)}
                          className="p-1.5 rounded hover:bg-red-500/20 text-red-400 transition-colors"
                          title="Supprimer"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2">
            <button disabled={page === 1} onClick={() => setPage(p => p - 1)} className="px-3 py-1.5 text-sm bg-card border border-border rounded-lg disabled:opacity-40 hover:bg-accent transition-colors">Précédent</button>
            <span className="text-sm text-muted-foreground">Page {page}/{totalPages}</span>
            <button disabled={page === totalPages} onClick={() => setPage(p => p + 1)} className="px-3 py-1.5 text-sm bg-card border border-border rounded-lg disabled:opacity-40 hover:bg-accent transition-colors">Suivant</button>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};
